import invariant from 'invariant';
import { useMemo } from 'react';

import type { ViewportBreakpoint } from 'theme/theme';
import { isDefined } from 'utils/functional/type';
import { viewportBreakpointArray } from 'utils/viewport/getViewportBreakpoint';

import { useViewportBreakpoint } from './useViewportBreakpoint';

export function useViewportBreakpointValue<T>(
  values: Partial<Record<ViewportBreakpoint, T>>,
): T {
  const valuesArray = useMemo(
    () => [values.xs, values.sm, values.md, values.lg],
    [values.xs, values.sm, values.md, values.lg],
  );

  const definedValuesArray = useMemo(
    () => valuesArray.filter(isDefined),
    [valuesArray],
  );

  invariant(
    definedValuesArray.length > 0,
    'Attempting to get viewport value without providing any breakpoint value',
  );

  const viewportBreakpoint = useViewportBreakpoint();

  return useMemo(() => {
    const viewportBreakpointIndex =
      viewportBreakpointArray.indexOf(viewportBreakpoint);

    for (let i = viewportBreakpointIndex; i >= 0; i--) {
      const value = valuesArray[i];
      if (typeof value !== 'undefined') return value;
    }

    return definedValuesArray[0];
  }, [viewportBreakpoint, definedValuesArray, valuesArray]);
}
